// src/components/VertexAISearchWidget.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import FirebaseService from '@/lib/firebase';
import { User } from 'firebase/auth';
import { Button } from "@/components/ui/button";
import { Search } from 'lucide-react';

// Declare the custom element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'gen-search-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        configId?: string;
        location?: string;
        triggerId?: string;
        authToken?: string;
        ref?: React.Ref<HTMLElement>;
      }, HTMLElement>;
    }
  }
}

// Define a custom interface for the widget element
interface VertexSearchWidgetElement extends HTMLElement {
  authToken?: string;
}

// Define props interface
interface VertexSearchWidgetProps {
  configId: string;
  buttonText?: string | null;
  className?: string;
  iconOnly?: boolean;
}

const VertexSearchWidget: React.FC<VertexSearchWidgetProps> = ({
  configId,
  buttonText = "Search Knowledge Base",
  className = "",
  iconOnly = false
}) => {
  
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const widgetRef = useRef<VertexSearchWidgetElement | null>(null);
  const scriptLoaded = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenRefreshTimer, setTokenRefreshTimer] = useState<NodeJS.Timeout | null>(null);
  
  // Additional refs to manage token fetch and application
  const tokenRequestInProgress = useRef(false);
  const latestToken = useRef<string | null>(null);
  const hasSetupWidget = useRef(false);
  const previousConfigId = useRef<string>(configId);

  //  handle configId changes
  useEffect(() => {
    // Check if configId has changed
    if (configId !== previousConfigId.current) {
      debugLog(`ConfigId changed from ${previousConfigId.current} to ${configId}`);
      previousConfigId.current = configId;
      
      // Reset widget by recreating it
      if (widgetRef.current) {
        debugLog('Resetting widget due to configId change');
        
        // Remove configId and save a reference to the element
        const widgetElement = widgetRef.current;
        widgetElement.removeAttribute('configId');
        
        // Apply token if available
        if (latestToken.current) {
          debugLog('Reapplying token to reset widget');
          widgetElement.authToken = latestToken.current;
        }
        
        // Short delay before adding the new configId
        setTimeout(() => {
          if (widgetElement) {
            debugLog(`Setting new configId: ${configId}`);
            widgetElement.setAttribute('configId', configId);
          }
        }, 100);
      }
    }
  }, [configId]);

  // Debug helper - only log in development
  const debugLog = (message: string) => {
    if (process.env.NODE_ENV === 'development' && process.env.NEXT_PUBLIC_DEBUG_VERTEX_SEARCH === 'true') {
      console.log(`[VertexSearch] ${message}`);
    }
  };
  
  // Function to fetch the search token from our backend
  const fetchSearchToken = useCallback(async (user: User) => {
    // Prevent multiple concurrent requests
    if (tokenRequestInProgress.current) {
      debugLog('Token request already in progress, skipping');
      return;
    }
    
    tokenRequestInProgress.current = true;
    debugLog(`Fetching token for user: ${user.email}`);
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Get Firebase ID token
      debugLog('Getting Firebase ID token...');
      const idToken = await user.getIdToken();
      debugLog('Firebase ID token obtained');
      
      // Call API endpoint
      debugLog('Calling API endpoint for Google token...');
      const response = await fetch('/api/aisearch', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      debugLog(`API response status: ${response.status}`);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Vertex AI Search authentication error: ${response.status} ${errorText}`);
        console.error(`To fix locally, run: gcloud auth application-default login`);
        setError('search-disabled');
        return; // Don't throw, just return early
      }
      
      const data = await response.json();
      debugLog('Token data received from API');
      
      // Store token regardless of widget status
      if (data.accessToken) {
        latestToken.current = data.accessToken;
        debugLog('Token stored in latestToken ref');
        
        // Apply token if widget exists
        if (widgetRef.current) {
          debugLog('Widget ref exists, applying token');
          widgetRef.current.authToken = data.accessToken;
          debugLog('Token applied to widget');
        } else {
          debugLog('Widget ref not available, token stored for later');
        }
        
        // Set up refresh timer
        if (tokenRefreshTimer) {
          debugLog('Clearing existing token refresh timer');
          clearTimeout(tokenRefreshTimer);
        }
        
        const refreshTime = (data.expiresIn || 3600) * 1000 - 5 * 60 * 1000;
        debugLog(`Setting refresh timer for ${refreshTime/1000} seconds from now`);
        const refreshTimer = setTimeout(() => {
          debugLog('Token refresh timer triggered');
          tokenRequestInProgress.current = false; // Reset flag before refreshing
          fetchSearchToken(user);
        }, refreshTime);
        
        setTokenRefreshTimer(refreshTimer);
      } else {
        debugLog('No access token in API response');
        throw new Error('No access token received from API');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Vertex AI Search token fetch error: ${errorMsg}`);
      setError('search-disabled');
      
      // Don't retry on authentication errors to avoid spamming logs
    } finally {
      setIsLoading(false);
      tokenRequestInProgress.current = false;
      debugLog('Token request completed');
    }
  }, [tokenRefreshTimer]);
  
  // Load widget script
  useEffect(() => {
    debugLog('Script loading effect running');
    if (!scriptLoaded.current) {
      debugLog('Loading widget script...');
      const script = document.createElement('script');
      script.src = 'https://cloud.google.com/ai/gen-app-builder/client?hl=en_US';
      script.async = true;
      script.onload = () => {
        debugLog('Widget script loaded successfully');
        scriptLoaded.current = true;
      };
      script.onerror = (error) => {
        debugLog(`Error loading widget script: ${error}`);
      };
      document.body.appendChild(script);
      
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, []);
  
  // Monitor Firebase authentication state
  useEffect(() => {
    debugLog('Auth state monitor effect running');
    const unsubscribe = FirebaseService.onAuthStateChange((user) => {
      debugLog(`Auth state changed, user: ${user ? `${user.email} (logged in)` : 'not logged in'}`);
      setIsAuthenticated(!!user);
      setCurrentUser(user);
      
      // Reset setup flag on auth change
      hasSetupWidget.current = false;
      
      // Try to immediately set up widget if user is available
      if (user) {
        debugLog('User authenticated, scheduling immediate token fetch');
        // Force token fetch on auth change
        setTimeout(() => {
          if (!tokenRequestInProgress.current) {
            debugLog('Delayed token fetch after auth change');
            fetchSearchToken(user);
          }
        }, 100); // Short delay to ensure component updates complete
      }
    });
    
    return () => {
      debugLog('Cleaning up auth state monitor');
      unsubscribe();
    };
  }, []);
  
  // Effect for initialization and setup
  useEffect(() => {
    debugLog(`Setup effect running: authenticated=${isAuthenticated}, scriptLoaded=${scriptLoaded.current}, hasSetupWidget=${hasSetupWidget.current}`);
    
    // Check if we can initialize
    if (isAuthenticated && currentUser && scriptLoaded.current && !hasSetupWidget.current) {
      debugLog('All conditions met for widget setup');
      hasSetupWidget.current = true;
      
      // Force fetch token
      debugLog('Triggering initial token fetch');
      fetchSearchToken(currentUser);
    }
  }, [isAuthenticated, currentUser, scriptLoaded.current]);
  
  // Effect to apply token when widget becomes available
  useEffect(() => {
    debugLog(`Widget ref effect: widgetRef=${!!widgetRef.current}, latestToken=${!!latestToken.current}`);
    if (widgetRef.current && latestToken.current) {
      debugLog('Applying stored token to widget from ref effect');
      widgetRef.current.authToken = latestToken.current;
    }
  }, [widgetRef]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshTimer) {
        debugLog('Component unmounting, clearing token refresh timer');
        clearTimeout(tokenRefreshTimer);
      }
    };
  }, [tokenRefreshTimer]);

  // Memoize the widget ref callback to prevent unnecessary re-renders
  const widgetRefCallback = useCallback((el: VertexSearchWidgetElement | null) => {
    debugLog(`Widget ref callback: element ${el ? 'exists' : 'is null'}`);
    if (el !== widgetRef.current) {
      widgetRef.current = el;
      debugLog('Updated widget ref');
      
      // Apply token if we already have one
      if (el && latestToken.current) {
        debugLog('Applying token to newly referenced widget');
        
        // Remove the configId first, then set the token, then re-add the configId
        // This forces the widget to start fresh with the token already set
        const currentConfigId = el.getAttribute('configId');
        el.removeAttribute('configId');
        
        // Apply token
        el.authToken = latestToken.current;
        
        // Wait a moment for the token to be processed, then re-add configId
        setTimeout(() => {
          if (el && currentConfigId) {
            debugLog('Re-adding configId after token was set');
            el.setAttribute('configId', currentConfigId);
          }
        }, 100);
      }
    }
  }, []);
  
  // If user is not authenticated, show login prompt
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center p-2 border border-gray-200 rounded-lg">
        <p className="mb-2 text-center text-sm">Sign in to access search</p>
        <Button 
          variant="outline"
          size="sm"
          onClick={() => FirebaseService.loginWithGoogle()}
          className="w-full"
        >
          Sign in with Google
        </Button>
      </div>
    );
  }
  
  // Show loading state
  if (isLoading && !latestToken.current) {
    return (
      <div className="flex items-center justify-center p-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
        <span className="ml-2 text-sm">Loading search...</span>
      </div>
    );
  }
  
  // Show disabled state on error
  if (error === 'search-disabled') {
    return (
      <Button
        variant="ghost"
        size="icon"
        disabled={true}
        className={`h-10 w-10 flex items-center justify-center border rounded-lg opacity-50 ${className}`}
        title="Search unavailable - authentication error"
      >
        <Search className="h-4 w-4" />
      </Button>
    );
  }
  
  return (
    <div className="search-widget-container">
      {/* Search button that triggers the widget */}
      <Button
        ref={searchButtonRef}
        id="searchWidgetTrigger"
        variant="ghost"
        size="icon"
        className={`h-10 w-10 flex items-center justify-center border rounded-lg ${className}`}
      >
        <Search className="h-4 w-4" />
        {!iconOnly && buttonText && <span className="ml-2">{buttonText}</span>}
      </Button>
      
      {/* The actual widget element */}
      {isAuthenticated && (
        <gen-search-widget
          ref={widgetRefCallback}
          configId={configId}
          location="eu"
          triggerId="searchWidgetTrigger">
        </gen-search-widget>
      )}
    </div>
  );
};

export default React.memo(VertexSearchWidget);